/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { DateRange } from '@customerio/fly-components/utils/fly-date-picker-utils';
import { action } from '@ember/object';
import { SortDirection } from 'cio/utils/sort';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import { LeaderBoardCardItem, LeaderboardCardTab } from 'cio/utils/components/leaderboard-card';
import { Periods } from 'cio/utils/metrics/common';
import AnalyticsService from 'cio/services/analytics';
import { getTimeRangeUtc } from 'cio/utils/time';

interface LeaderboardCardArgs {
  title: string;
  timeRange: DateRange;
  timezone: string;
  isLoading: boolean;
  resolution: Periods;
  items: LeaderBoardCardItem[];
  tabs: LeaderboardCardTab[];
  channels: string[];
  showValueTooltip?: boolean;
  showItemMetricTooltip?: boolean;
  shouldTrackDisplayChanged?: boolean;
  displayChangedEventCategory?: 'dashboard' | 'campaign';
  extraDisplayChangedEventData?: any;
  maxListItems?: number;
  onTrackNavigate?: (destination: string, sortDirection: SortDirection, activeTab: string, itemId: string) => unknown;
  showDashboardAnalysisLink?: boolean;
  tab: string;
  onTabChange: (activeTab: string) => unknown;
  sortDirection: SortDirection;
  onSortDirectionChange: (sortDirection: SortDirection) => unknown;
}

const DEFAULT_MAX_LIST_ITEMS = 5;

export default class LeaderboardCard extends Component<LeaderboardCardArgs> {
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;

  get maxListItems() {
    return this.args.maxListItems ?? DEFAULT_MAX_LIST_ITEMS;
  }

  get activeTabItem() {
    return this.args.tabs.find((i) => i.value === this.args.tab);
  }

  get showValueTooltip() {
    return this.args.showValueTooltip ?? true;
  }

  get showItemMetricTooltip() {
    return this.args.showItemMetricTooltip ?? false;
  }

  get utcTimeRange() {
    return getTimeRangeUtc(this.args.timeRange);
  }

  @action
  onTabChanged(tab: string) {
    if (this.args.onTabChange) {
      this.args.onTabChange(tab);
    }

    this.trackDisplayChanged('tab');
  }

  @action
  onSortDirectionChanged() {
    if (this.args.sortDirection === SortDirection.Ascending) {
      this.args.onSortDirectionChange(SortDirection.Descending);
    } else {
      this.args.onSortDirectionChange(SortDirection.Ascending);
    }

    this.trackDisplayChanged('sort_direction');
  }

  get isAllZeroes() {
    let totalKey = this.activeTabItem?.totalValue ?? `total_sent`;
    return !this.args.items.some((i) => {
      let total = i.data.delivery_metrics[totalKey];

      if (Array.isArray(total)) {
        return false;
      }

      return total > 0;
    });
  }

  get shouldShowBlankState() {
    return this.args.items.length === 0 || this.isAllZeroes;
  }

  get sortedMetrics() {
    if (!this.activeTabItem) {
      return [];
    }

    let sortDirectionValue = this.args.sortDirection === SortDirection.Ascending ? 1 : -1;

    return [...(this.args.items ?? [])]
      .sort((a, b) => {
        let aName = a.name;
        let bName = b.name;

        let nameCompare = aName.localeCompare(bName, undefined, { sensitivity: 'accent' });
        let aValue: number | null | undefined = null;
        let bValue: number | null | undefined = null;

        if (this.activeTabItem?.isRate) {
          let aRate = a?.data?.delivery_metrics?.[this.activeTabItem?.sortValue as string] as number | null | undefined;
          let bRate = b?.data?.delivery_metrics?.[this.activeTabItem?.sortValue as string] as number | null | undefined;

          if (aRate != null || bRate != null) {
            aValue = aRate;
            bValue = bRate;
          }
          // If both rates are null, it means that the denominator values are 0.
          // In this case, fall back to comparing the numerator totals.
        }

        if (aValue == null && bValue == null) {
          aValue = a?.data?.delivery_metrics?.[this.activeTabItem?.totalValue as string] as number;
          bValue = b?.data?.delivery_metrics?.[this.activeTabItem?.totalValue as string] as number;
        }

        // Sort non-null values higher
        if (aValue != null && bValue == null) {
          return sortDirectionValue;
        }

        if (aValue == null && bValue != null) {
          return -sortDirectionValue;
        }

        // This conditional shouldn't happen in practice, but it exists as a
        // fallback (and for type safety)
        if (aValue == null || bValue == null) {
          return nameCompare;
        }

        if (aValue === bValue) {
          return nameCompare;
        }

        return (aValue - bValue) * sortDirectionValue;
      })
      .slice(0, this.maxListItems);
  }

  @action
  trackDisplayChanged(editType: string) {
    if (!this.args.shouldTrackDisplayChanged || !this.args.displayChangedEventCategory) {
      return;
    }

    let data = {
      tab: this.args.tab,
      sortDirection: this.args.sortDirection,
      editType,
    };

    if (this.args.extraDisplayChangedEventData) {
      data = { ...data, ...this.args.extraDisplayChangedEventData };
    }

    this.analytics.track(this.args.displayChangedEventCategory, 'leaderboard_card_display_changed', data);
  }

  @action
  trackNavigate(destination: string, itemId: string) {
    if (!this.args.onTrackNavigate) {
      return;
    }
    this.args.onTrackNavigate(destination, this.args.sortDirection, this.args.tab, itemId);
  }
}
